/**
 * Unit shortcode extraction — the `<digits><2-3 CAPS>` prefix QuickBooks
 * customer names carry (e.g. "2371PD - (CLOSED) Stein" → "2371PD"). Pure and
 * dependency-free: shared by the server importer, the client-side
 * shortcode-lookup service helpers, and the QB bundle compiler.
 */

export const SHORTCODE_PATTERN = /(\d+)([A-Z]{2,3})/;

export function extractShortcode(text: string | null | undefined): string | null {
	if (!text) return null;
	const m = text.match(SHORTCODE_PATTERN);
	return m ? `${m[1]}${m[2]}` : null;
}

/**
 * Every shortcode-shaped token in a free-text string (an email subject), uppercased
 * and de-duplicated in order of appearance.
 *
 * Used to match a hand-forwarded homeowner thread back to an account: staff forward
 * with subjects like "FW: 3277NL Hyatts Village- Frere stormdoor", and RFC-5322
 * threading cannot help because the parent message only ever existed in their own
 * mailbox.
 *
 * Two details that look cosmetic but decide whether the RIGHT account is matched:
 *
 * - **The `-N` unit suffix is part of the token.** `3284NL` and `3284NL-1` are
 *   different accounts in the same association (2652 accounts carry a `-N`
 *   suffix), so truncating it would confidently match the wrong unit. The suffix
 *   is only taken when the digits follow the hyphen immediately — "3284NL -
 *   courtyard landscaping" is the bare account, not unit "courtyard".
 * - **A bare number is never a shortcode.** Accounts like "302" exist, but a
 *   stray digit in a subject line carries no identifying information, so the
 *   letters are required.
 */
const SUBJECT_SHORTCODE = /\b(\d{1,6})([A-Za-z]{2,4})(\([A-Za-z]\))?(-\d{1,3})?\b/g;

/**
 * Letter groups that are ordinals or times rather than unit codes, checked only
 * when the source text was lower-case.
 *
 * "meeting at 4pm" and "3277nl" are structurally identical — digits plus two
 * letters — so shape alone cannot separate them. Case does: the account
 * convention is upper-case (`3277NL`), while prose ordinals are lower-case
 * ("3rd", "15th"). This list is deliberately limited to suffixes that never occur
 * in the real chart: `ND`, `RD`, `PC` and `HR` are all genuine account suffixes
 * (192 accounts end in `PC`), so rejecting them outright would break real matches.
 */
const ORDINAL_OR_TIME = new Set(['am', 'pm', 'st', 'nd', 'rd', 'th']);

export function extractShortcodeCandidates(text: string | null | undefined): string[] {
	if (!text) return [];

	const seen = new Set<string>();
	const out: string[] = [];
	for (const m of text.matchAll(SUBJECT_SHORTCODE)) {
		const [, digits, letters, unitParen = '', unitSuffix = ''] = m;
		// Lower-case ordinals/times ("4pm", "3rd") are prose, not account codes.
		if (letters === letters.toLowerCase() && ORDINAL_OR_TIME.has(letters.toLowerCase())) continue;

		const token = `${digits}${letters}${unitParen}${unitSuffix}`.toUpperCase();
		if (seen.has(token)) continue;
		seen.add(token);
		out.push(token);
	}
	return out;
}
